import React, { useEffect, useState } from 'react'
import { ProjectComment } from '../../../../types/project'
import { useRequests } from '../../../../requests/useRequests'
import { RootState } from '../../../../state/store'
import { useSelector } from 'react-redux'
interface CommentSectionProps {
  projectId: string
}

const CommentSection: React.FC<CommentSectionProps> = ({
  projectId
}) => {
  const { username, id: currentUserId } = useSelector((state: RootState) => state.user)
  const requestMaker = useRequests()

  const [newComment, setNewComment] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [isEditingComment, setIsEditingComment] = useState<string | null>(null)
  const [editedComment, setEditedComment] = useState('')
  const [editedIsPrivate, setEditedIsPrivate] = useState(false)
  const [commentsFilter, setCommentsFilter] = useState<'all' | 'private' | 'public'>('all')
  const [isLoadingComments, setIsLoadingComments] = useState(false)
  const [comments, setComments] = useState<ProjectComment[]>([])

  const onAddComment = async (content: string, isPrivate: boolean): Promise<void> => {
    if (!content.trim()) return
    const success = await requestMaker.addProjectComment(projectId, { content, isPrivate })
    if (success) {
      setNewComment('')
      setIsPrivate(false)
      fetchProjectComments()
    }
  }

  const onUpdateComment = async (commentId: string, content: string, isPrivate: boolean): Promise<void> => {
    if (!content.trim()) return
    const success = await requestMaker.updateProjectComment(projectId, commentId, { content, isPrivate })
    if (success) {
      setIsEditingComment(null)
      setEditedComment('')
      setEditedIsPrivate(false)
      fetchProjectComments()
    }
  }

  const onDeleteComment = async (commentId: string): Promise<void> => {
    const success = await requestMaker.deleteProjectComment(projectId, commentId)
    if (success) {
      fetchProjectComments()
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return
    await onAddComment(newComment, isPrivate)
    setNewComment('')
    setIsPrivate(false)
  }

  const handleUpdateComment = async (commentId: string) => {
    if (!editedComment.trim()) return
    await onUpdateComment(commentId, editedComment, editedIsPrivate)
    setIsEditingComment(null)
    setEditedComment('')
    setEditedIsPrivate(false)
  }

  const handleDeleteComment = async (commentId: string) => {
    await onDeleteComment(commentId)
  }

  const fetchProjectComments = async () => {
    setIsLoadingComments(true)
    const response = await requestMaker.getProjectComments(projectId)
    setComments(response.data)
    setIsLoadingComments(false)
  }

  useEffect(() => {
    fetchProjectComments()
  }, [])

  if (!username) return null

  return (
    <div className='row mt-40'>
      <div className='col-sm-12'>
        <div className='card p-30'>
          <div className='projectCard'>
            <div className='d-flex justify-content-between align-items-center mb-20'>
              <h4 className='tw-font-semibold'>Notatki</h4>
            </div>

            <div className='form-group mb-3' style={{ maxWidth: 300 }}>
              <label htmlFor='commentsFilter' style={{ fontWeight: 500 }}>Filtruj notatki:</label>
              <select
                id='commentsFilter'
                className='form-control'
                value={commentsFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCommentsFilter(e.target.value as 'all' | 'private' | 'public')}
              >
                <option value='all'>Wszystkie</option>
                <option value='public'>Tylko publiczne</option>
                <option value='private'>Tylko prywatne</option>
              </select>
            </div>

            {isLoadingComments
              ? (
                <div className='text-center py-20'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='sr-only'>Ładowanie notatek...</span>
                  </div>
                </div>
                )
              : comments.filter(comment => {
                if (commentsFilter === 'all') return true
                if (commentsFilter === 'private') return comment.isPrivate
                if (commentsFilter === 'public') return !comment.isPrivate
                return true
              }).length === 0
                ? (
                  <div className='alert alert-info'>
                    Brak notatek. Dodaj pierwszą notatkę do tego projektu.
                  </div>
                  )
                : (
                  <div className='comments-list mb-30'>
                    {comments
                      .filter(comment => {
                        if (commentsFilter === 'all') return true
                        if (commentsFilter === 'private') return comment.isPrivate
                        if (commentsFilter === 'public') return !comment.isPrivate
                        return true
                      })
                      .map(comment => (
                        <div key={comment.id} className='comment-item tw-p-4 mb-15' style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                          <div className='tw-flex tw-justify-between'>
                            <div>
                              <h6 className='mb-1'>{comment.authorName}</h6>
                              <p className='text-muted small'>
                                <i className='fa fa-clock mr-1' />
                                {new Date(comment.createdAt).toLocaleString()}
                                {comment.createdAt !== comment.updatedAt && ' (edytowany)'}
                              </p>
                            </div>
                            {currentUserId === Number(comment.authorId) && (
                              <div>
                                {isEditingComment === comment.id
                  ? (
                                <div className='tw-flex tw-items-center tw-gap-x-2'>
                                  <button
                                    className='tw-text-blue-500 hover:tw-text-blue-700 focus:tw-text-blue-700'
                                    onClick={async () => await handleUpdateComment(comment.id)}
                                  >
                                    <i className='fa fa-save tw-text-lg' />
                                  </button>
                                  <button
                                    className='tw-text-red-500 hover:tw-text-red-700 focus:tw-text-red-700'
                                    onClick={() => {
                                      setIsEditingComment(null)
                                      setEditedComment('')
                                      setEditedIsPrivate(false)
                                    }}
                                  >
                                    <i className='fa fa-times tw-text-lg' />
                                  </button>
                                </div>
                    )
                  : (
                                <div className='tw-flex tw-items-center tw-gap-x-2'>
                                  <button
                                    className='tw-text-blue-500 hover:tw-text-blue-700 focus:tw-text-blue-700'
                                    onClick={() => {
                                      setIsEditingComment(comment.id)
                                      setEditedComment(comment.content)
                                      setEditedIsPrivate(comment.isPrivate)
                                    }}
                                  >
                                    <i className='fa fa-edit tw-text-lg' />
                                  </button>
                                  <button
                                    className='tw-text-red-500 hover:tw-text-red-700 focus:tw-text-red-700'
                                    onClick={async () => await handleDeleteComment(comment.id)}
                                  >
                                    <i className='fa fa-trash tw-text-lg' />
                                  </button>
                                </div>
                    )}
                              </div>
                            )}
                          </div>

                          {isEditingComment === comment.id
                            ? (
                              <div className='mt-2'>
                                <textarea
                  className='form-control'
                  value={editedComment}
                  onChange={(e) => setEditedComment(e.target.value)}
                  rows={3}
                />
                                <div className='form-check'>
                  <input
                                type='checkbox'
                                className='form-check-input'
                                id='editedIsPrivate'
                                checked={editedIsPrivate}
                                onChange={(e) => setEditedIsPrivate(e.target.checked)}
                              />
                  <label className='form-check-label' htmlFor='editedIsPrivate'>
                                Prywatny
                              </label>
                </div>
                                <div className='d-flex justify-content-end gap-2 mt-2'>
                  <button
                                className={`
                                  tw-mr-3 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                  tw-text-white
                                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                `}
                                onClick={() => {
                                  setIsEditingComment(null)
                                  setEditedComment('')
                                  setEditedIsPrivate(false)
                                }}
                              >
                                Anuluj
                              </button>
                  <button
                                type='button'
                                className={`
                                  tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                  tw-text-white
                                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                `}
                                onClick={async () => await handleUpdateComment(comment.id)}
                              >
                                Zapisz Zmiany
                              </button>
                </div>
                              </div>
                              )
                            : (
                              <div className='mt-2'>
                                <p>{comment.content}</p>
                              </div>
                              )}
                        </div>
                      ))}
                  </div>
                  )}

            <div className='add-comment-form'>
              <h5 className='mb-3 tw-font-semibold'>Dodaj komentarz</h5>
              <div className='form-group'>
                <textarea
                  className='form-control'
                  rows={3}
                  placeholder='Twój komentarz...'
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
              </div>
              <div className='form-check'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  id='isPrivate'
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='isPrivate'>
                  Prywatny
                </label>
              </div>
              <button
                type='button'
                className={`
                      tw-mt-3 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                      tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                    `}
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                Dodaj komentarz
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommentSection
